import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate } from 'react-router-dom';
import { getIslandCapabilities } from 'alakai-aloha-shared';
import useWeatherNormals from '../../hooks/useWeatherNormals';
import TripPlannerPromoHero from '../../components/shared/TripPlannerPromoHero';
import SoWhatPanel from '../../components/weather/SoWhatPanel';
import { buildRuntimeCanonicalUrl } from '../../lib/siteUrls';
import { getRuntimeSiteContext } from '../../lib/siteContext';
import { getDefaultWeatherLocationId } from '../../lib/weatherIslands';

// Lazy load the 3D component to avoid blocking initial render
const Weather3DHero = lazy(() => import('../../components/weather/Weather3DHero'));

// Loading fallback for the 3D component
const Hero3DFallback = () => (
  <div className="w-full h-[360px] md:h-[420px] rounded-lg flex items-center justify-center border border-white/15 bg-white/5 backdrop-blur">
    <div className="text-center">
      <div className="w-10 h-10 mx-auto mb-3 border-2 border-white/20 border-t-emerald-300 rounded-full animate-spin" />
      <p className="text-sm text-white/60">Loading 3D visualization...</p>
    </div>
  </div>
);

const MAX_LOCATIONS = 3;
const MONTH_KEYS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTH_LABELS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const METRIC_OPTIONS = [
  { id: 'temp', label: 'Temperature (High/Low/Avg)' },
  { id: 'rain', label: 'Rain (Total + Days with Rain)' },
  { id: 'cloud', label: 'Cloud Cover (Avg)' },
  { id: 'wind', label: 'Wind (Mean + Breezy Days)' },
  { id: 'swell', label: 'Swell (Height + Period)' },
];

const LOCATION_COLORS = ['#33E6D0', '#6DA7FF', '#F5C164'];

const METRIC_DETAILS = {
  temp: [
    { id: 'high', label: 'Highs' },
    { id: 'low', label: 'Lows' },
    { id: 'mean', label: 'Average' },
  ],
  rain: [
    { id: 'total', label: 'Total' },
    { id: 'rainyDays', label: 'Days with Rain' },
    { id: 'heavyRainDays', label: 'Heavy Rain Days' },
  ],
  cloud: [
    { id: 'mean', label: 'Average' },
  ],
  wind: [
    { id: 'mean', label: 'Mean Speed' },
    { id: 'breezyDays', label: 'Breezy Days' },
  ],
  swell: [
    { id: 'waveHeight', label: 'Wave Height' },
    { id: 'wavePeriod', label: 'Wave Period' },
  ],
};

const DEFAULT_DETAIL_BY_METRIC = {
  temp: 'high',
  rain: 'total',
  cloud: 'mean',
  wind: 'mean',
  swell: 'waveHeight',
};

const toF = (c) => (c * 9) / 5 + 32;
const mmToIn = (mm) => mm / 25.4;
const kmhToMph = (kmh) => kmh / 1.60934;
const mToFt = (m) => m * 3.28084;

const formatNumber = (value, decimals = 0) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return value.toFixed(decimals);
};

const formatRainThresholdLabel = (mm, unitSystem) => {
  if (!Number.isFinite(mm)) return '';
  if (unitSystem === 'imperial') {
    return `>${formatNumber(mmToIn(mm), 2)}"`;
  }
  return `>${formatNumber(mm, 0)}mm`;
};

const formatTemp = (value, unit) => {
  if (value === null || value === undefined) return '—';
  const converted = unit === 'imperial' ? toF(value) : value;
  return `${formatNumber(converted, 0)}°`;
};

const formatRain = (value, unit) => {
  if (value === null || value === undefined) return '—';
  const converted = unit === 'imperial' ? mmToIn(value) : value;
  const decimals = 0;
  const suffix = unit === 'imperial' ? 'in' : 'mm';
  return `${formatNumber(converted, decimals)} ${suffix}`;
};

const formatWind = (value, unit) => {
  if (value === null || value === undefined) return '—';
  const converted = unit === 'imperial' ? kmhToMph(value) : value;
  const suffix = unit === 'imperial' ? 'mph' : 'km/h';
  return `${formatNumber(converted, 0)} ${suffix}`;
};

const formatSwellHeight = (value, unit) => {
  if (value === null || value === undefined) return '—';
  const converted = unit === 'imperial' ? mToFt(value) : value;
  const suffix = unit === 'imperial' ? 'ft' : 'm';
  return `${formatNumber(converted, 0)} ${suffix}`;
};

const formatSwellPeriod = (value) => {
  if (value === null || value === undefined) return '—';
  return `${formatNumber(value, 0)} s`;
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return '—';
  return `${formatNumber(value, 0)}%`;
};

const formatDays = (value) => {
  if (value === null || value === undefined) return '—';
  return `${formatNumber(value, 0)} d`;
};

const toMonthIndex = (monthParam) => {
  if (!monthParam) return null;
  const normalized = monthParam.slice(0, 3).toLowerCase();
  const idx = MONTH_KEYS.findIndex(m => m.toLowerCase() === normalized);
  return idx >= 0 ? idx : null;
};

const getVisibleLocationIds = (ids) => {
  const lastSelectedIndex = ids.reduce((acc, id, idx) => (id ? idx : acc), -1);
  const count = Math.min(Math.max(lastSelectedIndex + 2, 1), MAX_LOCATIONS);
  const visible = ids.slice(0, count);
  while (visible.length < count) visible.push('');
  return visible;
};

const dedupeSelectedIds = (ids) => {
  const seen = new Set();
  return ids.map((id) => {
    if (!id) return '';
    if (seen.has(id)) return '';
    seen.add(id);
    return id;
  });
};

const WeatherVisualization = () => {
  const { data, loading, error } = useWeatherNormals();
  const location = useLocation();
  const navigate = useNavigate();
  const [initialized, setInitialized] = useState(false);

  const [mode, setMode] = useState('year');
  const [metric, setMetric] = useState('temp');
  const [metricDetail, setMetricDetail] = useState(DEFAULT_DETAIL_BY_METRIC.temp);
  const [unitSystem, setUnitSystem] = useState('imperial');
  const [monthIndex, setMonthIndex] = useState(0);
  const { islandConfig } = React.useMemo(() => getRuntimeSiteContext(), []);
  const capabilities = useMemo(() => getIslandCapabilities(islandConfig.key), [islandConfig.key]);
  const [selectedIds, setSelectedIds] = useState([]);
  const selectedKey = selectedIds.join('|');

  const locationsByIsland = useMemo(() => {
    if (!data?.locations) return {};
    return data.locations.reduce((acc, loc) => {
      if (!acc[loc.islandLabel]) acc[loc.islandLabel] = [];
      acc[loc.islandLabel].push(loc);
      return acc;
    }, {});
  }, [data]);

  const selectedLocations = useMemo(() => {
    if (!data?.locations) return [];
    return selectedIds
      .map(id => data.locations.find(loc => loc.id === id))
      .filter(Boolean);
  }, [data, selectedIds]);

  const derivedScales = useMemo(() => {
    if (!data?.scales) return data?.scales;
    const next = {
      ...data.scales,
      temperatureC: {
        ...data.scales.temperatureC,
      },
    };
    if (!next.temperatureC.mean && data?.locations?.length) {
      let min = Infinity;
      let max = -Infinity;
      data.locations.forEach((loc) => {
        loc.monthly?.temperatureC?.mean?.forEach((value) => {
          if (value === null || value === undefined || Number.isNaN(value)) return;
          min = Math.min(min, value);
          max = Math.max(max, value);
        });
      });
      if (Number.isFinite(min) && Number.isFinite(max)) {
        next.temperatureC.mean = { min, max };
      }
    }
    return next;
  }, [data]);

  useEffect(() => {
    if (!data || initialized) return;
    const params = new URLSearchParams(location.search);
    const ids = ['a', 'b', 'c']
      .map(key => params.get(key))
      .filter(Boolean)
      .filter(id => data.locations.some(loc => loc.id === id));
    const nextIds = ids.length
      ? ids.slice(0, MAX_LOCATIONS)
      : [getDefaultWeatherLocationId(data.locations, islandConfig.key), ''].filter(Boolean);

    const monthParam = params.get('month');
    const monthIdx = toMonthIndex(monthParam);

    const modeParam = params.get('mode');
    const metricParam = params.get('metric');
    const detailParam = params.get('detail');

    const nextMetric = METRIC_OPTIONS.some(opt => opt.id === metricParam) ? metricParam : 'temp';
    const detailOptions = METRIC_DETAILS[nextMetric] || [];
    const nextDetail = detailOptions.some(opt => opt.id === detailParam)
      ? detailParam
      : (detailOptions[0]?.id || DEFAULT_DETAIL_BY_METRIC[nextMetric]);

    setSelectedIds(dedupeSelectedIds(nextIds));
    if (monthIdx !== null) setMonthIndex(monthIdx);
    if (modeParam === 'trip') setMode('trip');
    setMetric(nextMetric);
    setMetricDetail(nextDetail);
    setInitialized(true);
  }, [data, initialized, islandConfig.key, location.search]);

  useEffect(() => {
    if (!initialized) return;
    const params = new URLSearchParams();
    selectedIds.filter(Boolean).forEach((id, idx) => {
      params.set(String.fromCharCode(97 + idx), id);
    });
    params.set('month', MONTH_KEYS[monthIndex].toLowerCase());
    params.set('mode', mode);
    params.set('metric', metric);
    if (metricDetail) params.set('detail', metricDetail);
    navigate({ search: params.toString() }, { replace: true });
  }, [initialized, selectedIds, monthIndex, mode, metric, metricDetail, navigate]);

  useEffect(() => {
    const options = METRIC_DETAILS[metric] || [];
    if (!options.length) return;
    if (!options.some(opt => opt.id === metricDetail)) {
      setMetricDetail(options[0].id);
    }
  }, [metric, metricDetail]);

  const handleLocationChange = (index, value) => {
    const next = [...selectedIds];
    if (value && next.some((id, idx) => id === value && idx !== index)) {
      return;
    }
    next[index] = value;
    if (!value) {
      for (let i = index + 1; i < next.length; i += 1) {
        next[i] = '';
      }
    }
    setSelectedIds(dedupeSelectedIds(next.slice(0, MAX_LOCATIONS)));
  };

  const dataWindow = data?.source?.dateWindow;
  const dataWindowLabel = dataWindow ? `${dataWindow.start.slice(0, 4)}–${dataWindow.end.slice(0, 4)}` : '';
  const definitions = data?.source?.definitions || {};
  const yearLeftRowRefs = useRef([]);
  const yearRightRowRefs = useRef([]);
  const tripLeftRowRefs = useRef([]);
  const tripRightRowRefs = useRef([]);
  const rainThresholdMm = Number.isFinite(definitions.rainyDayThresholdMm) ? definitions.rainyDayThresholdMm : 1;
  const heavyRainThresholdMm = Number.isFinite(definitions.heavyRainDayThresholdMm) ? definitions.heavyRainDayThresholdMm : 10;
  const rainThresholdLabel = formatRainThresholdLabel(rainThresholdMm, unitSystem);
  const heavyRainThresholdLabel = formatRainThresholdLabel(heavyRainThresholdMm, unitSystem);
  const breezyThresholdValue = unitSystem === 'imperial'
    ? definitions.breezyDayThresholdMph
    : definitions.breezyDayThresholdKmh;
  const breezyThresholdLabel = breezyThresholdValue
    ? `${Math.round(breezyThresholdValue)} ${unitSystem === 'imperial' ? 'mph' : 'km/h'}`
    : null;

  const metricDetailOptions = useMemo(() => {
    const options = METRIC_DETAILS[metric] || [];
    if (metric !== 'rain') return options;
    return options.map((option) => {
      if (option.id === 'rainyDays') {
        return { ...option, label: `Days with Rain (${rainThresholdLabel})` };
      }
      if (option.id === 'heavyRainDays') {
        return { ...option, label: `Heavy Rain Days (${heavyRainThresholdLabel})` };
      }
      return option;
    });
  }, [metric, rainThresholdLabel, heavyRainThresholdLabel]);

  const syncRowHeights = useCallback((leftRefs, rightRefs) => {
    leftRefs.current.forEach((el) => {
      if (el) el.style.minHeight = '';
    });
    rightRefs.current.forEach((el) => {
      if (el) el.style.minHeight = '';
    });
    const rowCount = Math.max(leftRefs.current.length, rightRefs.current.length);
    for (let i = 0; i < rowCount; i += 1) {
      const left = leftRefs.current[i];
      const right = rightRefs.current[i];
      if (!left || !right) continue;
      const max = Math.max(left.offsetHeight, right.offsetHeight);
      left.style.minHeight = `${max}px`;
      right.style.minHeight = `${max}px`;
    }
  }, []);

  useLayoutEffect(() => {
    if (!selectedLocations.length) return;
    if (mode === 'year') {
      syncRowHeights(yearLeftRowRefs, yearRightRowRefs);
    } else {
      syncRowHeights(tripLeftRowRefs, tripRightRowRefs);
    }
  }, [selectedKey, selectedLocations.length, mode, metric, metricDetail, unitSystem, monthIndex, syncRowHeights]);

  useEffect(() => {
    if (!selectedLocations.length) return undefined;
    const handleResize = () => {
      if (mode === 'year') {
        syncRowHeights(yearLeftRowRefs, yearRightRowRefs);
      } else {
        syncRowHeights(tripLeftRowRefs, tripRightRowRefs);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [selectedKey, selectedLocations.length, mode, syncRowHeights]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16 text-white">
        <div className="text-center text-lg text-white/80">Loading weather normals…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-5xl mx-auto px-6 py-16 text-white">
        <div className="text-center text-rose-200">Failed to load weather data.</div>
      </div>
    );
  }

  const yearMetricRenderer = (loc, monthIdx) => {
    const monthly = loc.monthly;
    if (!monthly) return '—';
    if (metric === 'temp') {
      const high = monthly.temperatureC?.high?.[monthIdx];
      const low = monthly.temperatureC?.low?.[monthIdx];
      return (
        <div className="flex flex-col gap-1 leading-tight">
          <span>{formatTemp(high, unitSystem)}</span>
          <span className="text-white/60 text-xs">{formatTemp(low, unitSystem)}</span>
        </div>
      );
    }
    if (metric === 'rain') {
      const total = monthly.rainMm?.total?.[monthIdx];
      const days = monthly.rainMm?.rainyDays?.[monthIdx];
      const heavyDays = monthly.rainMm?.heavyRainDays?.[monthIdx];
      return (
        <div className="flex flex-col gap-1 leading-tight">
          <span>{formatRain(total, unitSystem)}</span>
          <span className="text-white/60 text-xs">{formatDays(days)}</span>
          {heavyDays !== null && heavyDays !== undefined ? (
            <span className="text-white/60 text-xs">{formatDays(heavyDays)}</span>
          ) : null}
        </div>
      );
    }
    if (metric === 'cloud') {
      const cloud = monthly.cloudCoverPct?.mean?.[monthIdx];
      return formatPercent(cloud);
    }
    if (metric === 'wind') {
      const mean = monthly.windKmh?.mean?.[monthIdx];
      const breezy = monthly.windKmh?.breezyDays?.[monthIdx];
      return (
        <div className="flex flex-col gap-1 leading-tight">
          <span>{formatWind(mean, unitSystem)}</span>
          <span className="text-white/60 text-xs">{formatDays(breezy)}</span>
        </div>
      );
    }
    if (metric === 'swell') {
      const height = monthly.swell?.waveHeightM?.mean?.[monthIdx];
      const period = monthly.swell?.wavePeriodS?.mean?.[monthIdx];
      return (
        <div className="flex flex-col gap-1 leading-tight">
          <span>{formatSwellHeight(height, unitSystem)}</span>
          <span className="text-white/60 text-xs">{formatSwellPeriod(period)}</span>
        </div>
      );
    }
    return '—';
  };

  const yearTableTitle = (() => {
    switch (metric) {
      case 'temp':
        return (
          <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold text-white">Average Temperature by Month</span>
            <span className="pl-3 text-sm text-white/80">
              High ({unitSystem === 'imperial' ? '°F' : '°C'})
            </span>
            <span className="pl-3 text-xs text-white/60">
              Low ({unitSystem === 'imperial' ? '°F' : '°C'})
            </span>
          </div>
        );
      case 'rain':
        return (
          <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold text-white">Average Rainfall by Month</span>
            <span className="pl-3 text-sm text-white/80">Total Rain</span>
            <span className="pl-3 text-xs text-white/60">Days with Rain {rainThresholdLabel}</span>
            <span className="pl-3 text-xs text-white/60">Heavy Rain Days {heavyRainThresholdLabel}</span>
          </div>
        );
      case 'cloud':
        return <span className="text-sm font-semibold text-white">Average Cloud Cover by Month</span>;
      case 'wind':
        return (
          <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold text-white">Average Wind by Month</span>
            <span className="pl-3 text-sm text-white/80">Speed</span>
            <span className="pl-3 text-xs text-white/60">
              # of Breezy Days{breezyThresholdLabel ? ` (> ${breezyThresholdLabel})` : ''}
            </span>
          </div>
        );
      case 'swell':
        return (
          <div className="flex flex-col gap-1">
            <span className="text-sm font-semibold text-white">Average Swell by Month</span>
            <span className="pl-3 text-sm text-white/80">Wave Height</span>
            <span className="pl-3 text-xs text-white/60">Period</span>
          </div>
        );
      default:
        return <span className="text-sm font-semibold text-white">Average Conditions by Month</span>;
    }
  })();

  const tripMetrics = [
    {
      label: `Avg High (${unitSystem === 'imperial' ? '°F' : '°C'})`,
      getValue: (loc) => formatTemp(loc.monthly.temperatureC?.high?.[monthIndex], unitSystem),
    },
    {
      label: `Avg Low (${unitSystem === 'imperial' ? '°F' : '°C'})`,
      getValue: (loc) => formatTemp(loc.monthly.temperatureC?.low?.[monthIndex], unitSystem),
    },
    {
      label: `Rain Total (${unitSystem === 'imperial' ? 'in' : 'mm'})`,
      getValue: (loc) => formatRain(loc.monthly.rainMm?.total?.[monthIndex], unitSystem),
    },
    {
      label: `Days with Rain (${rainThresholdLabel})`,
      getValue: (loc) => formatDays(loc.monthly.rainMm?.rainyDays?.[monthIndex]),
    },
    {
      label: `Heavy Rain Days (${heavyRainThresholdLabel})`,
      getValue: (loc) => formatDays(loc.monthly.rainMm?.heavyRainDays?.[monthIndex]),
    },
    {
      label: 'Cloud Cover',
      getValue: (loc) => formatPercent(loc.monthly.cloudCoverPct?.mean?.[monthIndex]),
    },
    {
      label: `Wind Mean (${unitSystem === 'imperial' ? 'mph' : 'km/h'})`,
      getValue: (loc) => formatWind(loc.monthly.windKmh?.mean?.[monthIndex], unitSystem),
    },
    {
      label: 'Breezy Days',
      getValue: (loc) => formatDays(loc.monthly.windKmh?.breezyDays?.[monthIndex]),
    },
    {
      label: `Swell Height (${unitSystem === 'imperial' ? 'ft' : 'm'})`,
      getValue: (loc) => formatSwellHeight(loc.monthly.swell?.waveHeightM?.mean?.[monthIndex], unitSystem),
    },
    {
      label: 'Swell Direction',
      getValue: (loc) => {
        const dir = loc.monthly.swell?.waveDirectionDeg?.dominant?.[monthIndex];
        return dir !== null && dir !== undefined ? `${dir}°` : '—';
      },
    },
    {
      label: 'Swell Period (s)',
      getValue: (loc) => formatSwellPeriod(loc.monthly.swell?.wavePeriodS?.mean?.[monthIndex]),
    },
  ];

  const pageTitle = `${islandConfig.title} Weather Comparison | Alaka‘i Aloha`;
  const pageDescription = `Compare monthly temperature, rain, wind, cloud cover, and swell across ${islandConfig.title} and the rest of Hawaiʻi.`;
  const pageUrl = buildRuntimeCanonicalUrl('/weather');

  return (
    <div className="max-w-6xl mx-auto px-6 pt-6 pb-16 text-white">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="twitter:card" content="summary" />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={pageDescription} />
      </Helmet>
      <div className="space-y-6">
        <div className="text-center space-y-1">
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">{`${islandConfig.title} Weather Comparison`}</h1>
        </div>

        <div className="space-y-3">
          <div className="grid gap-3 md:grid-cols-3">
            <div className="flex items-center gap-2 w-full">
              <span className="text-xs uppercase tracking-wide text-white/70">Mode</span>
              <div className="inline-flex rounded-full border border-white/20 overflow-hidden bg-white/5 backdrop-blur">
                <button
                  type="button"
                  onClick={() => setMode('year')}
                  className={`px-4 py-1 text-sm ${mode === 'year' ? 'bg-white/20 text-white' : 'text-white/80'}`}
                >
                  Year
                </button>
                <button
                  type="button"
                  onClick={() => setMode('trip')}
                  className={`px-4 py-1 text-sm ${mode === 'trip' ? 'bg-white/20 text-white' : 'text-white/80'}`}
                >
                  Trip Month
                </button>
              </div>
            </div>

            {mode === 'trip' && (
              <div className="flex items-center gap-2 w-full">
                <span className="text-xs uppercase tracking-wide text-white/70">Month</span>
                <select
                  value={monthIndex}
                  onChange={(e) => setMonthIndex(parseInt(e.target.value, 10))}
                  className="border border-white/20 rounded-md px-3 py-1 text-sm bg-white/5 text-white backdrop-blur"
                >
                  {MONTH_KEYS.map((month, idx) => (
                    <option key={month} value={idx} className="bg-slate-900 text-white">{MONTH_LABELS[idx]}</option>
                  ))}
                </select>
              </div>
            )}

            {mode === 'year' && (
              <>
                <div className="flex items-center gap-2 w-full">
                  <span className="text-xs uppercase tracking-wide text-white/70">Metric</span>
                  <select
                    value={metric}
                    onChange={(e) => setMetric(e.target.value)}
                    className="border border-white/20 rounded-md px-3 py-1 text-sm bg-white/5 text-white backdrop-blur"
                  >
                    {METRIC_OPTIONS.map(option => (
                      <option key={option.id} value={option.id} className="bg-slate-900 text-white">{option.label}</option>
                    ))}
                  </select>
                </div>
              </>
            )}

            <div className="flex items-center gap-2 w-full">
              <span className="text-xs uppercase tracking-wide text-white/70">Units</span>
              <div className="inline-flex rounded-full border border-white/20 overflow-hidden bg-white/5 backdrop-blur">
                <button
                  type="button"
                  onClick={() => setUnitSystem('imperial')}
                  className={`px-4 py-1 text-sm ${unitSystem === 'imperial' ? 'bg-white/20 text-white' : 'text-white/80'}`}
                >
                  Imperial
                </button>
                <button
                  type="button"
                  onClick={() => setUnitSystem('metric')}
                  className={`px-4 py-1 text-sm ${unitSystem === 'metric' ? 'bg-white/20 text-white' : 'text-white/80'}`}
                >
                  Metric
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          {getVisibleLocationIds(selectedIds).map((id, idx) => {
            const disabledIds = new Set(
              selectedIds.filter((selectedId, selectedIdx) => selectedId && selectedIdx !== idx),
            );
            return (
              <div key={`loc-${idx}`} className="flex items-center gap-2">
                <span
                  className="h-2.5 w-2.5 rounded-full border border-white/20"
                  style={{ backgroundColor: id ? LOCATION_COLORS[idx % LOCATION_COLORS.length] : 'transparent' }}
                />
                <select
                  value={id || ''}
                  onChange={(e) => handleLocationChange(idx, e.target.value)}
                  className="w-full border border-white/20 rounded-md px-3 py-2 text-sm bg-white/5 text-white backdrop-blur"
                >
                  <option value="" className="bg-slate-900 text-white">Select a location</option>
                  {Object.entries(locationsByIsland).map(([island, items]) => (
                    <optgroup key={island} label={island} className="bg-slate-900 text-white">
                      {items.map(loc => (
                        <option
                          key={loc.id}
                          value={loc.id}
                          disabled={disabledIds.has(loc.id)}
                          className="bg-slate-900 text-white"
                        >
                          {loc.label}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>
            );
          })}
        </div>

        {selectedLocations.length > 0 && (
          <div className="space-y-6">
            <Suspense fallback={<Hero3DFallback />}>
              <Weather3DHero
                scales={derivedScales}
                selectedLocations={selectedLocations}
                mode={mode}
                metric={metric}
                metricDetail={metricDetail}
                metricDetailOptions={metricDetailOptions}
                onMetricDetailChange={setMetricDetail}
                monthIndex={monthIndex}
                unitSystem={unitSystem}
              />
            </Suspense>
          </div>
        )}

        {selectedLocations.length > 0 && (
          <SoWhatPanel
            locationIds={selectedLocations.map(loc => loc.id)}
            mode={mode}
            monthIndex={monthIndex}
            unitSystem={unitSystem}
          />
        )}

        {selectedLocations.length > 0 && mode === 'year' ? (
          <div className="space-y-3">
            <div>{yearTableTitle}</div>
            <div className="border border-white/15 rounded-lg bg-white/5 backdrop-blur overflow-hidden text-[13px] text-white">
              <div className="grid grid-cols-[minmax(110px,150px)_1fr] sm:grid-cols-[minmax(140px,200px)_1fr] md:grid-cols-[minmax(180px,260px)_1fr]">
                <div className="border-r border-white/10 bg-white/5">
                  <div
                    ref={(el) => { yearLeftRowRefs.current[0] = el; }}
                    className="p-3 text-left text-sm text-white/70 bg-white/5"
                  >
                    Location
                  </div>
                  {selectedLocations.map((loc, idx) => (
                    <div
                      key={loc.id}
                      ref={(el) => { yearLeftRowRefs.current[idx + 1] = el; }}
                      className="p-2 md:p-3 font-medium border-t border-white/10"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className="h-2 w-2 rounded-full"
                          style={{ backgroundColor: LOCATION_COLORS[idx % LOCATION_COLORS.length] }}
                        />
                        <span>{loc.label}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="overflow-x-auto">
                  <div className="min-w-[520px] md:min-w-[720px]">
                    <div
                      ref={(el) => { yearRightRowRefs.current[0] = el; }}
                      className="grid grid-cols-12 bg-white/5 text-white/70"
                    >
                      {MONTH_KEYS.map((month) => (
                        <div key={month} className="p-3 text-center">{month}</div>
                      ))}
                    </div>
                    {selectedLocations.map((loc, rowIdx) => (
                      <div
                        key={`${loc.id}-row`}
                        ref={(el) => { yearRightRowRefs.current[rowIdx + 1] = el; }}
                        className="grid grid-cols-12 border-t border-white/10"
                      >
                        {MONTH_KEYS.map((month, monthIdx) => (
                          <div key={`${loc.id}-${month}`} className="p-2 md:p-3 text-center whitespace-nowrap text-white/80">
                            {yearMetricRenderer(loc, monthIdx)}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {selectedLocations.length > 0 && mode !== 'year' ? (
          <div className="border border-white/15 rounded-lg bg-white/5 backdrop-blur overflow-hidden text-[13px] text-white">
            <div className="grid grid-cols-[minmax(110px,150px)_1fr] sm:grid-cols-[minmax(140px,200px)_1fr] md:grid-cols-[minmax(180px,260px)_1fr]">
              <div className="border-r border-white/10 bg-white/5">
                <div
                  ref={(el) => { tripLeftRowRefs.current[0] = el; }}
                  className="p-3 text-left text-sm text-white/70 bg-white/5"
                >
                  Metric
                </div>
                {tripMetrics.map((metricRow, idx) => (
                  <div
                    key={metricRow.label}
                    ref={(el) => { tripLeftRowRefs.current[idx + 1] = el; }}
                    className="p-2 md:p-3 font-medium border-t border-white/10"
                  >
                    {metricRow.label}
                  </div>
                ))}
              </div>
              <div className="overflow-x-auto">
                <div style={{ minWidth: `${Math.max(selectedLocations.length * 105, 260)}px` }}>
                  <div
                    ref={(el) => { tripRightRowRefs.current[0] = el; }}
                    className="grid bg-white/5 text-white/70"
                    style={{ gridTemplateColumns: `repeat(${selectedLocations.length}, minmax(100px, 1fr))` }}
                  >
                    {selectedLocations.map((loc, idx) => (
                      <div key={`trip-${loc.id}`} className="p-2 md:p-3 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <span
                            className="h-2 w-2 rounded-full"
                            style={{ backgroundColor: LOCATION_COLORS[idx % LOCATION_COLORS.length] }}
                          />
                          <span>{loc.shortLabel}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  {tripMetrics.map((metricRow, rowIdx) => (
                    <div
                      key={`${metricRow.label}-row`}
                      ref={(el) => { tripRightRowRefs.current[rowIdx + 1] = el; }}
                      className="grid border-t border-white/10"
                      style={{ gridTemplateColumns: `repeat(${selectedLocations.length}, minmax(100px, 1fr))` }}
                    >
                      {selectedLocations.map(loc => (
                        <div key={`${metricRow.label}-${loc.id}`} className="p-2 md:p-3 text-center text-white/80">
                          {metricRow.getValue(loc)}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {selectedLocations.length > 0 && (
          <div className="text-xs text-white/55">
            Monthly averages based on NOAA GHCN-Daily stations for temperature/rain plus Open-Meteo reanalysis for cloud, wind, and swell ({dataWindowLabel}).
          </div>
        )}
      </div>

      {capabilities.tripPlanner || capabilities.stayQuiz ? (
        <div className="mt-16">
          <TripPlannerPromoHero pageType="region" />
        </div>
      ) : null}
    </div>
  );
};

export default WeatherVisualization;
